import { api } from './client';
import type { ApiResponse, PaginatedResponse } from './types';

// ============================================================================
// Types
// ============================================================================

export type NostalgiaEra = 'era_70s' | 'era_80s' | 'era_90s' | 'era_2000s' | 'era_2010s';
export type NostalgiaTheme = 'tv_shows' | 'movies' | 'music' | 'snacks' | 'games' | 'school' | 'festivals' | 'cricket' | 'radio' | 'fashion' | 'toys' | 'other';
export type BoardItemType = 'text' | 'image' | 'video' | 'audio' | 'link' | 'sticker' | 'poll' | 'quote' | 'then_now' | 'artifact';
export type LayoutMode = 'grid' | 'collage' | 'story' | 'free';
export type BoardVisibility = 'everyone' | 'my_state' | 'connections';

export interface NostalgiaBoard {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  userId: string;
  era: NostalgiaEra | null;
  region: string | null;
  theme: NostalgiaTheme | null;
  tags: string[];
  coverImageUrl: string | null;
  layoutMode: LayoutMode;
  templateId: string | null;
  backgroundColor: string | null;
  backgroundImageUrl: string | null;
  isCollaborative: boolean;
  viewCount: number;
  itemCount: number;
  reactionCounts: Record<string, number>;
  visibility: BoardVisibility;
  isFeatured: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    name: string;
    avatarUrl: string | null;
  };
  commentCount?: number;
  topMemory?: { text: string } | null;
  panelImages?: { url: string }[];
}

export interface BoardItem {
  id: string;
  boardId: string;
  itemType: BoardItemType;
  content: Record<string, unknown>;
  sortOrder: number;
  slotIndex: number | null;
  isPlaceholder: boolean;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  zIndex: number;
  rotation: number;
  libraryItemId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NostalgiaLibraryItem {
  id: string;
  category: string;
  name: string;
  imageUrl: string | null;
  audioUrl: string | null;
  era: NostalgiaEra | null;
  description: string | null;
  tags: string[];
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
}

export interface NostalgiaSticker {
  id: string;
  stickerId: string;
  name: string;
  imageUrl: string;
  category: string;
  era: NostalgiaEra | null;
  isActive: boolean;
  createdAt: string;
}

export interface BoardTemplate {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  layoutMode: LayoutMode;
  slotCount: number;
  slotConfig: unknown[];
  defaultEra: NostalgiaEra | null;
  defaultTheme: NostalgiaTheme | null;
  backgroundColor: string | null;
  previewImageUrl: string | null;
  category: string | null;
  isActive: boolean;
}

export interface BoardComment {
  id: string;
  boardId: string;
  userId: string;
  content: string;
  isActive: boolean;
  createdAt: string;
  user?: {
    id: string;
    name: string;
    avatarUrl: string | null;
  };
}

// ============================================================================
// API Functions - Boards
// ============================================================================

export interface ListBoardsParams {
  q?: string;
  era?: NostalgiaEra;
  theme?: NostalgiaTheme;
  region?: string;
  featured?: boolean;
  userId?: string;
  sort?: 'view_count' | 'created_at' | 'reaction_count';
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

interface BoardsListResponse {
  success: boolean;
  data: {
    boards: NostalgiaBoard[];
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export async function listBoards(params?: ListBoardsParams): Promise<PaginatedResponse<NostalgiaBoard>> {
  const response = await api.get<BoardsListResponse>('/nostalgia/boards', params as Record<string, string | number | boolean | undefined>);
  // Transform API response to match PaginatedResponse type
  return {
    success: response.success,
    data: response.data.boards,
    meta: response.data.meta,
  };
}

// Extended board type that includes items from getBoard endpoint
export interface NostalgiaBoardWithItems extends NostalgiaBoard {
  items?: BoardItem[];
  comments?: BoardComment[];
}

interface BoardResponse {
  success: boolean;
  data: {
    board: NostalgiaBoardWithItems;
  };
}

export async function getBoard(idOrSlug: string): Promise<ApiResponse<NostalgiaBoardWithItems>> {
  const response = await api.get<BoardResponse>(`/nostalgia/boards/${idOrSlug}`);
  return {
    success: response.success,
    data: response.data.board,
  };
}

export interface CreateBoardInput {
  title: string;
  description?: string;
  era?: NostalgiaEra;
  region?: string;
  theme?: NostalgiaTheme;
  tags?: string[];
  coverImageUrl?: string;
  isCollaborative?: boolean;
  visibility?: BoardVisibility;
  layoutMode?: LayoutMode;
  templateId?: string;
  backgroundColor?: string;
  backgroundImageUrl?: string;
}

interface BoardMutationResponse {
  success: boolean;
  data: {
    board: NostalgiaBoard;
  };
}

export async function createBoard(input: CreateBoardInput): Promise<ApiResponse<NostalgiaBoard>> {
  const response = await api.post<BoardMutationResponse>('/nostalgia/boards', input);
  return {
    success: response.success,
    data: response.data.board,
  };
}

export async function updateBoard(id: string, input: Partial<CreateBoardInput>): Promise<ApiResponse<NostalgiaBoard>> {
  const response = await api.patch<BoardMutationResponse>(`/nostalgia/boards/${id}`, input);
  return {
    success: response.success,
    data: response.data.board,
  };
}

export async function deleteBoard(id: string) {
  return api.delete<ApiResponse<void>>(`/nostalgia/boards/${id}`);
}

// ============================================================================
// API Functions - Board Items
// ============================================================================

// Note: Items are included in the getBoard response, not a separate endpoint
// This function extracts items from the board response
export async function listBoardItems(boardId: string): Promise<ApiResponse<BoardItem[]>> {
  const boardResponse = await getBoard(boardId);
  return {
    success: boardResponse.success,
    data: boardResponse.data.items || [],
  };
}

export interface CreateItemInput {
  itemType: BoardItemType;
  content: Record<string, unknown>;
  sortOrder?: number;
  slotIndex?: number;
  isPlaceholder?: boolean;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  zIndex?: number;
  rotation?: number;
  libraryItemId?: string;
}

interface ItemMutationResponse {
  success: boolean;
  data: {
    item: BoardItem;
  };
}

/**
 * One call copies a catalogue sticker onto a board (POST /nostalgia/boards/{id}/stickers/{stickerId}):
 * the item carries the sticker's address and name, so the board renders without a second lookup.
 * One `idempotencyKey` per copy; resending with the same key must return the same item.
 */
export async function copyStickerToBoard(
  boardId: string,
  stickerRowId: string,
  where: { positionX?: number; positionY?: number; idempotencyKey?: string } = {},
): Promise<ApiResponse<BoardItem>> {
  const response = await api.post<ItemMutationResponse>(`/nostalgia/boards/${boardId}/stickers/${stickerRowId}`, where);
  return { success: response.success, data: response.data.item };
}

export async function createBoardItem(boardId: string, input: CreateItemInput): Promise<ApiResponse<BoardItem>> {
  const response = await api.post<ItemMutationResponse>(`/nostalgia/boards/${boardId}/items`, input);
  return {
    success: response.success,
    data: response.data.item,
  };
}

export async function updateBoardItem(boardId: string, itemId: string, input: Partial<CreateItemInput>): Promise<ApiResponse<BoardItem>> {
  const response = await api.patch<ItemMutationResponse>(`/nostalgia/boards/${boardId}/items/${itemId}`, input);
  return {
    success: response.success,
    data: response.data.item,
  };
}

export async function deleteBoardItem(boardId: string, itemId: string) {
  return api.delete<ApiResponse<void>>(`/nostalgia/boards/${boardId}/items/${itemId}`);
}

// ============================================================================
// API Functions - Comments
// ============================================================================

interface CommentsListResponse {
  success: boolean;
  data: {
    comments: BoardComment[];
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export async function listBoardComments(boardId: string): Promise<ApiResponse<BoardComment[]>> {
  const response = await api.get<CommentsListResponse>(`/nostalgia/boards/${boardId}/comments`);
  return {
    success: response.success,
    data: response.data.comments,
  };
}

interface CommentMutationResponse {
  success: boolean;
  data: {
    comment: BoardComment;
  };
}

export async function createBoardComment(boardId: string, content: string): Promise<ApiResponse<BoardComment>> {
  const response = await api.post<CommentMutationResponse>(`/nostalgia/boards/${boardId}/comments`, { content });
  return {
    success: response.success,
    data: response.data.comment,
  };
}

/**
 * Comments delete by their OWN id, not through the board.
 *
 * This used to call `/nostalgia/boards/{boardId}/comments/{commentId}`, which
 * no route has ever served — every delete from the Lab was a silent 404. The
 * board id is not a parameter here at all; a comment id is already unique.
 */
export async function deleteBoardComment(commentId: string) {
  return api.delete<ApiResponse<{ message: string }>>(`/nostalgia/comments/${commentId}`);
}

// ============================================================================
// API Functions - Library
// ============================================================================

export interface ListLibraryParams {
  category?: string;
  era?: NostalgiaEra;
  q?: string;
  page?: number;
  limit?: number;
}

interface LibraryListResponse {
  success: boolean;
  data: {
    items: NostalgiaLibraryItem[];
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export async function listLibrary(params?: ListLibraryParams): Promise<PaginatedResponse<NostalgiaLibraryItem>> {
  const response = await api.get<LibraryListResponse>('/nostalgia/library', params as Record<string, string | number | boolean | undefined>);
  return {
    success: response.success,
    data: response.data.items,
    meta: response.data.meta,
  };
}

// Note: Library items are typically seeded/admin-managed
// These endpoints may need to be created if not existing

// ============================================================================
// API Functions - Stickers
// ============================================================================

export interface ListStickersParams {
  category?: string;
  era?: NostalgiaEra;
}

interface StickersListResponse {
  success: boolean;
  data: {
    stickers: NostalgiaSticker[];
  };
}

export async function listStickers(params?: ListStickersParams): Promise<ApiResponse<NostalgiaSticker[]>> {
  const response = await api.get<StickersListResponse>('/nostalgia/stickers', params as Record<string, string | number | boolean | undefined>);
  return {
    success: response.success,
    data: response.data.stickers,
  };
}

// ============================================================================
// API Functions - Templates
// ============================================================================

export interface ListTemplatesParams {
  category?: string;
  layoutMode?: LayoutMode;
}

interface TemplatesListResponse {
  success: boolean;
  data: {
    templates: BoardTemplate[];
  };
}

export async function listTemplates(params?: ListTemplatesParams): Promise<ApiResponse<BoardTemplate[]>> {
  const response = await api.get<TemplatesListResponse>('/nostalgia/templates', params as Record<string, string | number | boolean | undefined>);
  return {
    success: response.success,
    data: response.data.templates,
  };
}

// ============================================================================
// API Functions - Prompts
// ============================================================================

// ============================================================================
// Memories — the library as a set of CUES, not a blank box.
//
// Nostalgia is triggered, not summoned: nobody opens an empty field and
// remembers something. So the shape is a shared cue (a Rasna ad, a Doordarshan
// title card) that people tap "I remember this" on, and only some of them
// then write.
//
// Two counts, and the gap between them is the point: `rememberCount` is
// public and large, `writtenCount` is always far smaller. WHO tapped is never
// exposed — the count is public, the tap is private.
//
// `iRemember` is NULLABLE and null means "we do not know because you are not
// signed in", which is not the same as false. Rendering null as an untapped
// button tells a signed-out reader they have not done something we cannot see.
// ============================================================================

export interface NostalgiaCue {
  id: string;
  name: string;
  /** Open string — the catalogue grows. */
  category: string;
  era: string | null;
  imageUrl: string | null;
  audioUrl: string | null;
  description: string | null;
  tags: string[];
}

export interface NostalgiaMemory {
  id: string;
  body: string | null;
  imageUrl: string | null;
  isAnonymous: boolean;
  createdAt: string;
  /** Null when anonymous — the author is REMOVED, not hidden behind a flag. */
  author: { id: string; name: string; avatarUrl: string | null; city: string | null } | null;
}

export interface NostalgiaCueDetail {
  item: NostalgiaCue;
  rememberCount: number;
  writtenCount: number;
  iRemember: boolean | null;
  myMemory: Omit<NostalgiaMemory, "author"> | null;
  recentMemories: NostalgiaMemory[];
}

export function getLibraryCategories() {
  return api.get<ApiResponse<{ categories: { category: string; count: number }[] }>>(
    "/nostalgia/library/categories",
  );
}

export function getLibraryByCategory(category: string) {
  return api.get<ApiResponse<{ items: NostalgiaCue[] }>>(`/nostalgia/library/${category}`);
}

export function getLibraryItem(id: string) {
  return api.get<ApiResponse<NostalgiaCueDetail>>(`/nostalgia/library/items/${id}`);
}

export function listItemMemories(id: string, params?: { limit?: number; offset?: number }) {
  return api.get<
    ApiResponse<{
      memories: NostalgiaMemory[];
      meta: { total: number; limit: number; offset: number; hasMore: boolean };
    }>
  >(
    `/nostalgia/library/items/${id}/memories`,
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function rememberItem(id: string) {
  return api.post<ApiResponse<{ iRemember: boolean; rememberCount: number }>>(
    `/nostalgia/library/items/${id}/remember`,
    {},
  );
}

export function forgetItem(id: string) {
  return api.delete<ApiResponse<{ iRemember: boolean; rememberCount: number }>>(
    `/nostalgia/library/items/${id}/remember`,
  );
}

export function writeMemory(
  id: string,
  input: { body: string; imageUrl?: string | null; isAnonymous?: boolean },
) {
  // PUT, not POST — one memory per person per cue, so writing again replaces.
  return api.put<ApiResponse<{ memory: NostalgiaMemory; rememberCount: number }>>(
    `/nostalgia/library/items/${id}/memory`,
    input,
  );
}

export function clearMemory(id: string) {
  return api.delete<ApiResponse<void>>(`/nostalgia/library/items/${id}/memory`);
}

// ============================================================================
// Boards — the shelves, and the arranging
// ============================================================================

export const BOARD_REACTIONS = ["remember", "miss_this", "so_true", "feels"] as const;
export type BoardReaction = (typeof BOARD_REACTIONS)[number];

export function getTrendingBoards() {
  return api.get<ApiResponse<{ boards: NostalgiaBoard[] }>>("/nostalgia/boards/trending");
}

export function getFeaturedBoards() {
  return api.get<ApiResponse<{ boards: NostalgiaBoard[] }>>("/nostalgia/boards/featured");
}

export function getTemplate(idOrSlug: string) {
  return api.get<ApiResponse<{ template: BoardTemplate }>>(`/nostalgia/templates/${idOrSlug}`);
}

export function reorderBoardItems(
  boardId: string,
  items: { id: string; sortOrder: number; slotIndex?: number }[],
) {
  return api.post<ApiResponse<{ success: boolean; updatedCount: number }>>(
    `/nostalgia/boards/${boardId}/items/reorder`,
    { items },
  );
}

export function updateBoardLayout(
  boardId: string,
  layoutMode: "grid" | "collage" | "story" | "free",
) {
  return api.patch<ApiResponse<{ id: string; layoutMode: string; updatedAt: string }>>(
    `/nostalgia/boards/${boardId}/layout`,
    { layoutMode },
  );
}

export function reactToBoard(boardId: string, reactionType: BoardReaction) {
  // `action` comes back as 'added' or 'exists' — reacting twice is not an error.
  return api.post<
    ApiResponse<{ success: boolean; reactionType: BoardReaction; action: string }>
  >(`/nostalgia/boards/${boardId}/react`, { reactionType });
}

export function removeBoardReaction(boardId: string, type: BoardReaction) {
  // The reaction type is in the PATH here, not the body — you remove one kind.
  return api.delete<ApiResponse<{ message: string }>>(
    `/nostalgia/boards/${boardId}/react/${type}`,
  );
}

export async function listPrompts(era?: NostalgiaEra) {
  return api.get<ApiResponse<string[]>>('/nostalgia/prompts', era ? { era } : undefined);
}

// ============================================================================
// Constants for UI
// ============================================================================

export const ERA_OPTIONS = [
  { value: 'era_70s', label: '70s' },
  { value: 'era_80s', label: '80s' },
  { value: 'era_90s', label: '90s' },
  { value: 'era_2000s', label: '2000s' },
  { value: 'era_2010s', label: '2010s' },
];

export const THEME_OPTIONS = [
  { value: 'tv_shows', label: 'TV Shows' },
  { value: 'movies', label: 'Movies' },
  { value: 'music', label: 'Music' },
  { value: 'snacks', label: 'Snacks' },
  { value: 'games', label: 'Games' },
  { value: 'school', label: 'School' },
  { value: 'festivals', label: 'Festivals' },
  { value: 'cricket', label: 'Cricket' },
  { value: 'radio', label: 'Radio' },
  { value: 'fashion', label: 'Fashion' },
  { value: 'toys', label: 'Toys' },
  { value: 'other', label: 'Other' },
];

export const LAYOUT_OPTIONS = [
  { value: 'grid', label: 'Grid' },
  { value: 'collage', label: 'Collage' },
  { value: 'story', label: 'Story' },
  { value: 'free', label: 'Free Canvas' },
];

export const VISIBILITY_OPTIONS = [
  { value: 'everyone', label: 'Everyone' },
  { value: 'my_state', label: 'My State Only' },
  { value: 'connections', label: 'Connections Only' },
];

export const ITEM_TYPE_OPTIONS = [
  { value: 'text', label: 'Text/Memory' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'link', label: 'Link' },
  { value: 'sticker', label: 'Sticker' },
  { value: 'poll', label: 'Poll' },
  { value: 'quote', label: 'Quote' },
  { value: 'then_now', label: 'Then & Now' },
  { value: 'artifact', label: 'Artifact' },
];

export const LIBRARY_CATEGORY_OPTIONS = [
  { value: 'tv_shows', label: 'TV Shows' },
  { value: 'movies', label: 'Movies' },
  { value: 'music', label: 'Music' },
  { value: 'snacks', label: 'Snacks' },
  { value: 'games', label: 'Games' },
  { value: 'toys', label: 'Toys' },
  { value: 'brands', label: 'Brands' },
  { value: 'celebrities', label: 'Celebrities' },
];
